/** Edit the durable history boundary for one automatic Kuali module. */
import { useEffect, useState } from 'react'
import type { ReactNode } from 'react'

import { Input, RadioGroup, RadioGroupItem } from '@/components/ui'
import { cn } from '@/lib/utils'
import type { KualiResearchModuleOptions } from '../types'

type StartingPoint = 'all' | 'after' | 'forward'

export function KualiResearchStartingPoint({
  module,
  onChange,
}: {
  module: KualiResearchModuleOptions
  onChange: (module: KualiResearchModuleOptions) => void
}) {
  const [value, setValue] = useState<StartingPoint>(module.startAfter ? 'after' : 'all')

  useEffect(() => {
    setValue(module.startAfter ? 'after' : 'all')
  }, [module.source])

  const choose = (next: StartingPoint) => {
    setValue(next)
    const today = new Date().toISOString().slice(0, 10)
    onChange({
      ...module,
      startAfter: next === 'all' ? null : next === 'forward' ? today : module.startAfter ?? today,
    })
  }

  return (
    <fieldset className="space-y-1.5">
      <legend className="text-[12px] font-semibold text-slate-700">Starting point</legend>
      <RadioGroup value={value} onValueChange={next => choose(next as StartingPoint)} className="gap-1.5">
        <StartingPointRow value="all" current={value} label="All awards" detail="Backfill full history, then keep up" />
        <StartingPointRow value="after" current={value} label="After a date" detail="Awards effective on or after">
          <Input
            aria-label="Starting date"
            type="date"
            value={module.startAfter ?? ''}
            onChange={event => onChange({ ...module, startAfter: event.target.value || null })}
            className="h-[30px] w-[132px] rounded-[8px] px-2 text-[12px]"
          />
        </StartingPointRow>
        <StartingPointRow value="forward" current={value} label="Going forward" detail="Only new activity from now on" />
      </RadioGroup>
    </fieldset>
  )
}

function StartingPointRow({
  value,
  current,
  label,
  detail,
  children,
}: {
  value: StartingPoint
  current: StartingPoint
  label: string
  detail: string
  children?: ReactNode
}) {
  return (
    <label className={cn(
      'flex min-h-[36px] cursor-pointer items-center gap-2.5 rounded-[9px] border bg-white px-3 py-1.5',
      current === value ? 'border-[1.5px] border-slate-900' : 'border-slate-200',
    )}>
      <RadioGroupItem value={value} />
      <span className="text-[12px] font-semibold text-slate-900">{label}</span>
      <span className="min-w-0 flex-1 text-[11.5px] text-slate-400">{detail}</span>
      {children}
    </label>
  )
}
